import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Zap, Check, RotateCw, PartyPopper } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { MathText } from "../components/MathText";
import { useT } from "../context/LanguageContext";

export default function QuickReview() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [done, setDone] = useState(false);

  const load = () => api.get(`/exams/${examId}/quick-review`).then((r) => {
    setQuiz(r.data); setIdx(0); setPicked(null); setCorrect(0); setDone(false);
  }).catch((err) => { toast.error(formatApiError(err.response?.data?.detail)); navigate(`/exam/${examId}`); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [examId]);

  if (!quiz) return <div className="space-bg min-h-screen flex items-center justify-center text-white/60">{t("Caricamento ripasso veloce...")}</div>;

  if (done) {
    const pct = Math.round((correct / quiz.questions.length) * 100);
    return (
      <div className="space-bg min-h-screen flex items-center justify-center px-5">
        <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="glass rounded-3xl p-8 max-w-md text-center" data-testid="quick-done-card">
          <PartyPopper size={44} style={{ color: "var(--cyan)" }} className="mx-auto mb-3" />
          <h2 className="font-heading font-black text-3xl tracking-tight mb-1">{correct}/{quiz.questions.length}</h2>
          <p className="text-white/60 mb-6">{t("Ripasso veloce completato · ")}{pct}%</p>
          <div className="space-y-3">
            <button onClick={load} className="w-full py-3 rounded-2xl btn-violet font-heading font-bold bouncy" data-testid="quick-again-btn">{t("Nuovo ripasso veloce")}</button>
            <button onClick={() => navigate(`/exam/${examId}`)} className="w-full py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="quick-back-btn">{t("Torna all'esame")}</button>
          </div>
        </motion.div>
      </div>
    );
  }

  const q = quiz.questions[idx];
  const answer = (oi) => {
    if (picked !== null) return;
    setPicked(oi);
    if (oi === quiz.answers[idx]) setCorrect((c) => c + 1);
  };
  const next = () => {
    if (idx + 1 >= quiz.questions.length) { setDone(true); return; }
    setIdx(idx + 1); setPicked(null);
  };

  return (
    <div className="space-bg min-h-screen flex flex-col">
      <header className="glass sticky top-0 z-40 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(`/exam/${examId}`)} className="text-white/60 bouncy"><X size={22} /></button>
        <div className="flex-1 mx-4 h-2.5 rounded-full bg-black/40 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${(idx / quiz.questions.length) * 100}%`, background: "var(--cyan)" }} />
        </div>
        <span className="flex items-center gap-1 font-num text-sm"><Zap size={16} style={{ color: "var(--cyan)" }} /> {idx + 1}/{quiz.questions.length}</span>
      </header>

      <div className="flex-1 max-w-lg w-full mx-auto px-4 py-6 flex flex-col">
        <span className="text-xs uppercase tracking-widest text-white/40 font-heading mb-2">{t("Ripasso veloce")}</span>
        <motion.div key={idx} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="glass rounded-3xl p-6 mb-5" data-testid="quick-question">
          <p className="font-heading font-bold text-lg tracking-tight"><MathText>{q.q}</MathText></p>
        </motion.div>
        <div className="space-y-3 flex-1">
          {q.o.map((opt, oi) => {
            const isCorrect = picked !== null && oi === quiz.answers[idx];
            const isWrong = picked !== null && oi === picked && oi !== quiz.answers[idx];
            return (
              <motion.button key={oi} whileTap={{ scale: 0.97 }} onClick={() => answer(oi)} disabled={picked !== null}
                className="w-full text-left px-5 py-4 rounded-2xl font-body font-semibold flex items-center justify-between glass"
                style={{ border: isCorrect ? "2px solid var(--success)" : isWrong ? "2px solid var(--danger)" : "1px solid rgba(255,255,255,0.12)",
                  background: isCorrect ? "rgba(0,255,127,0.15)" : isWrong ? "rgba(255,51,102,0.15)" : undefined }}
                data-testid={`quick-answer-${oi}`}>
                <span><MathText>{opt}</MathText></span>
                {isCorrect && <Check size={20} style={{ color: "var(--success)" }} />}
                {isWrong && <X size={20} style={{ color: "var(--danger)" }} />}
              </motion.button>
            );
          })}
        </div>
        {picked !== null && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
            {quiz.explanations[idx] && <p className="text-sm text-white/60 mb-3 glass rounded-2xl p-3"><MathText>{quiz.explanations[idx]}</MathText></p>}
            <button onClick={next} className="w-full py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="quick-next-btn">
              {idx + 1 >= quiz.questions.length ? t("Termina") : t("Avanti")}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
